/**
 * Navbar Store
 *
 * Zustand-based store for navbar with command-driven architecture.
 *
 * Usage in components:
 * ```typescript
 * import {
 *   useNavbarDispatch,
 *   useNavbarSelector,
 *   useNavbarPolicies,
 *   createNavbarPolicies
 * } from '@domains/navbar';
 * import useBackend from '@contexts/useBackend/useBackend';
 *
 * function Layout() {
 *   const { backend } = useBackend();
 *   const dispatch = useNavbarDispatch();
 *
 *   // Initialize policies with backend
 *   useNavbarPolicies( createNavbarPolicies( backend ));
 *
 *   // Open mobile drawer
 *   const handleToggleDrawer = () => {
 *     dispatch(
 *       { type: 'navbar/drawer/toggle', payload: {} },
 *       { source: 'user', timestamp: Date.now() }
 *     );
 *   };
 * }
 * ```
 */

import { createStore } from '@commandDrivenStateEngine/core/createStore';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { initialState } from './state';
import { handlers } from './handlers';
import { invariants } from './invariants';
import { createAccountSubscriptionPolicy } from './policies/accountSubscriptionPolicy';
import { createAppLoginQRPolicy } from './policies/appLoginQRPolicy';

// Create the store
const {
  useSelector,
  useErrors,
  useDispatch,
  usePolicies,
  getState,
  dispatch
} = createStore({
  name: 'navbar',
  initialState,
  handlers,
  invariants
});

/**
 * Policy factory function.
 * Creates policies with backend dependency.
 *
 * @param backend - Backend client from useBackend()
 */
export const createNavbarPolicies = ( backend: ClientLibraryType ) => [
  createAccountSubscriptionPolicy( backend ),
  createAppLoginQRPolicy( backend )
];

// Export hooks with domain-specific names
export {
  useSelector as useNavbarSelector,
  useErrors as useNavbarErrors,
  useDispatch as useNavbarDispatch,
  usePolicies as useNavbarPolicies,
  getState as getNavbarState,
  dispatch as dispatchNavbarCommand
};

// Re-export selectors
export { selectors } from './state';
